/**
 * Cart recommendations — Section Rendering API based on first cart product.
 * Pattern from Yarbo US cart-recommendations.js, adapted for Ray Neo cart events.
 */
const saCartRecsCache = new Map();
const SA_CART_RECS_MIN_LOADING_MS = 400;

const SA_CART_RECS_SKELETON = `
  <div class="sa-cart-recs__skeleton flex gap-12" aria-hidden="true">
    <div class="h-220 min-w-0 flex-1 animate-pulse rounded-2xl border border-[#CCCCCC] bg-white"></div>
    <div class="h-220 min-w-0 flex-1 animate-pulse rounded-2xl border border-[#CCCCCC] bg-white"></div>
  </div>
`;

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.recommendationsUrl = this.dataset.recommendationsUrl;
    this.sectionId = this.dataset.sectionId || 'sa-cart-recommendations';
    this.swiperInstance = null;
    this.loaded = false;
    this._loading = false;
    this._onCartUpdated = this.onCartUpdated.bind(this);
  }

  connectedCallback() {
    this._drawer = this.closest('cart-drawer');
    document.addEventListener('cart:updated', this._onCartUpdated);

    // Re-attached after cart DOM patch — restore swiper, do not refetch
    if (this.loaded && this.querySelector('.sa-cart-recs__swiper .swiper-slide')) {
      this.setReady();
      this.initNav();
      this.initSwiper();
      return;
    }

    // Start immediately so skeleton is visible while the drawer animates open
    this.loadRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._onCartUpdated);
    this.destroySwiper();
  }

  showLoading() {
    const content = this.querySelector('[data-sa-cart-recs-content]');
    if (content) {
      content.innerHTML = SA_CART_RECS_SKELETON;
    }

    const headerNav = this.querySelector('.sa-cart-recs__header .sa-cart-recs__nav');
    if (headerNav) {
      headerNav.hidden = true;
      headerNav.innerHTML = '';
    }

    this.classList.remove('sa-cart-recs--ready');
    this.classList.add('sa-cart-recs--loading');
    this.removeAttribute('hidden');
    this.hidden = false;
    this.setAttribute('aria-busy', 'true');
  }

  setReady() {
    this.classList.remove('sa-cart-recs--loading');
    this.classList.add('sa-cart-recs--ready');
    this.removeAttribute('hidden');
    this.hidden = false;
    this.removeAttribute('aria-busy');
  }

  setEmpty() {
    this.classList.remove('sa-cart-recs--loading', 'sa-cart-recs--ready');
    this.hidden = true;
    this.setAttribute('hidden', '');
    this.removeAttribute('aria-busy');
  }

  onCartUpdated(event) {
    const cart = event.detail?.cart;
    const items = cart?.items;

    if (cart?.item_count === 0) {
      saCartRecsCache.delete(this.getCacheKey());
      this.setEmpty();
      this.loaded = false;
      this._loading = false;
      this.productId = null;
      this.destroySwiper();
      return;
    }

    if (!items || items.length === 0) return;

    // Only re-render when the first cart product changes
    const newFirstProductId = String(items[0].product_id);
    if (newFirstProductId === String(this.productId || '')) return;

    const previousCacheKey = this.getCacheKey();
    this.productId = newFirstProductId;
    this.dataset.productId = this.productId;
    this.loaded = false;
    this._loading = false;
    this.destroySwiper();
    saCartRecsCache.delete(previousCacheKey);

    this.loadRecommendations();
  }

  getSectionUrl() {
    return `${this.recommendationsUrl}?product_id=${this.productId}&limit=10&intent=complementary&section_id=${this.sectionId}`;
  }

  hasSwiperSlides(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sectionContent = parsed.querySelector('.shopify-section');
    if (!sectionContent) return false;
    return Boolean(sectionContent.querySelector('.sa-cart-recs__swiper .swiper-slide'));
  }

  applyFallback(content) {
    const fallback = this.querySelector('[data-sa-cart-recs-fallback]');
    if (!fallback?.content?.querySelector('.swiper-slide')) return false;

    content.innerHTML = '';
    content.appendChild(fallback.content.cloneNode(true));
    return true;
  }

  async ensureMinLoadingTime(startedAt) {
    const elapsed = Date.now() - startedAt;
    if (elapsed >= SA_CART_RECS_MIN_LOADING_MS) return;
    await new Promise((resolve) => setTimeout(resolve, SA_CART_RECS_MIN_LOADING_MS - elapsed));
  }

  mountSwiper(content) {
    if (!content.querySelector('.sa-cart-recs__swiper .swiper-slide')) return false;

    this.loaded = true;
    this.setReady();
    this.initNav();
    this.initSwiper();
    return true;
  }

  getCacheKey() {
    return `${this.productId}-${this.sectionId}`;
  }

  mountFromCache(content) {
    const cachedHtml = saCartRecsCache.get(this.getCacheKey());
    if (!cachedHtml) return false;

    content.innerHTML = cachedHtml;
    return this.mountSwiper(content);
  }

  async loadRecommendations() {
    if (!this.productId || this.loaded || this._loading) return;

    const content = this.querySelector('[data-sa-cart-recs-content]');
    if (!content) return;

    this._loading = true;
    const startedAt = Date.now();

    // Cache: still show a short skeleton so reopening doesn't pop content
    if (saCartRecsCache.has(this.getCacheKey())) {
      this.showLoading();
      await this.ensureMinLoadingTime(startedAt);
      this.mountFromCache(content);
      this._loading = false;
      return;
    }

    this.showLoading();

    const useFallback = this.dataset.useFallback === 'true';

    try {
      const response = await fetch(this.getSectionUrl());
      if (response.ok) {
        const html = await response.text();
        if (this.hasSwiperSlides(html)) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const sectionContent = parsed.querySelector('.shopify-section');
          saCartRecsCache.set(this.getCacheKey(), sectionContent.innerHTML);
          await this.ensureMinLoadingTime(startedAt);
          content.innerHTML = sectionContent.innerHTML;
          if (this.mountSwiper(content)) {
            this._loading = false;
            return;
          }
        }
      }
    } catch (error) {
      console.error('Cart recommendations error:', error);
    }

    if (useFallback && this.applyFallback(content)) {
      await this.ensureMinLoadingTime(startedAt);
      if (this.mountSwiper(content)) {
        this._loading = false;
        return;
      }
    }

    this.setEmpty();
    this.loaded = true;
    this._loading = false;
  }

  initNav() {
    const headerNav = this.querySelector('.sa-cart-recs__header .sa-cart-recs__nav');
    const swiper = this.querySelector('.sa-cart-recs__swiper');
    if (!headerNav || !swiper) return;

    const slides = swiper.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      headerNav.hidden = true;
      headerNav.innerHTML = '';
      return;
    }

    headerNav.hidden = false;
    headerNav.innerHTML = `
      <button type="button" class="sa-cart-recs__nav-btn sa-cart-recs__nav-btn--prev" aria-label="Previous">
        <svg width="5" height="10" viewBox="0 0 5 10" fill="none" aria-hidden="true"><path d="M4.5 0.5L0.5 5L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>
      </button>
      <button type="button" class="sa-cart-recs__nav-btn sa-cart-recs__nav-btn--next" aria-label="Next">
        <svg width="5" height="10" viewBox="0 0 5 10" fill="none" aria-hidden="true"><path d="M0.5 0.5L4.5 5L0.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>
      </button>
    `;
  }

  initSwiper() {
    this.destroySwiper();

    const swiperEl = this.querySelector('.sa-cart-recs__swiper');
    const prevBtn = this.querySelector('.sa-cart-recs__nav-btn--prev');
    const nextBtn = this.querySelector('.sa-cart-recs__nav-btn--next');
    if (!swiperEl || typeof Swiper === 'undefined') return;

    this.swiperInstance = new Swiper(swiperEl, {
      slidesPerView: 2.1,
      spaceBetween: 12,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      breakpoints: {
        768: {
          slidesPerView: 'auto',
          spaceBetween: 14,
        },
        1560: {
          slidesPerView: 'auto',
          spaceBetween: 16,
        },
      },
    });
  }

  destroySwiper() {
    if (!this.swiperInstance) return;
    this.swiperInstance.destroy(true, true);
    this.swiperInstance = null;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
